import type { JSX, ComponentPropsWithoutRef } from 'react';
import {
  Root as RadixSliderRoot,
  Track as RadixSliderTrack,
  Range as RadixSliderRange,
  Thumb as RadixSliderThumb,
} from '@radix-ui/react-slider';
import { styled } from 'styled-components';
import { isNonEmptyString } from '@wistia/type-guards';

const SliderContainer = styled.div`
  --wui-slider-track-color: var(--wui-gray-6);
  --wui-slider-track-border-radius: var(--wui-border-radius-rounded);
  --wui-slider-range-color: var(--wui-color-bg-fill);
  --wui-slider-thumb-color: var(--wui-color-bg-surface);
  --wui-slider-thumb-border-color: var(--wui-color-border-secondary);

  [data-disabled] {
    --wui-slider-range-color: var(--wui-gray-6);
    --wui-slider-track-color: var(--wui-color-bg-surface-disabled);
    --wui-slider-thumb-color: var(--wui-color-bg-surface-disabled);
    --wui-slider-thumb-border-color: var(--wui-color-border-disabled);

    pointer-events: none;
  }
`;

const StyledSliderRoot = styled(RadixSliderRoot)`
  position: relative;
  display: flex;
  align-items: center;
  user-select: none;
  touch-action: none;
  width: 100%;
`;

const StyledSliderTrack = styled(RadixSliderTrack)`
  background-color: var(--wui-slider-track-color);
  border-radius: var(--wui-slider-track-border-radius);
  flex-grow: 1;
  height: 6px;
  position: relative;
`;

const StyledSliderRange = styled(RadixSliderRange)`
  background-color: var(--wui-slider-range-color);
  border-radius: var(--wui-slider-track-border-radius);
  height: 100%;
  position: absolute;
`;

const StyledSliderThumb = styled(RadixSliderThumb)`
  background-color: var(--wui-slider-thumb-color);
  border-radius: var(--wui-border-radius-rounded);
  cursor: grab;
  display: block;
  height: 16px;
  outline-color: var(--wui-slider-thumb-border-color);
  outline-style: solid;
  outline-width: 1px;
  transition-duration: 70ms;
  transition-property: border-width, box-shadow, transform;
  transition-timing-function: var(--wui-motion-ease-out);
  width: 16px;

  &:hover,
  &:active {
    transform-origin: center;
    transform: scale(1.2);
    transition-timing-function: var(--wui-motion-ease-in);
  }

  &:focus-visible {
    outline-color: var(--wui-color-focus-ring);
    outline-offset: -1px;
    outline-width: 2px;
  }
`;

export type SliderProps = Omit<ComponentPropsWithoutRef<'div'>, 'onChange'> & {
  /**
   * aria-label for screen-readers to apply to the slider
   */
  'aria-label'?: string;
  /**
   * aria-labelledby for screen-readers to apply to the slider
   */
  'aria-labelledby'?: string;
  /**
   * @ignore
   */
  'data-testid'?: string;
  /**
   * Determines the initial position(s) of the thumb(s)
   * Must be an array of numbers.
   */
  initialValue?: number[];
  /**
   * If `true` the thumbs can't be moved.
   */
  disabled?: boolean;
  /**
   * The maximum value of the slider.
   */
  max?: number;
  /**
   * The minimum value of the slider.
   */
  min?: number;
  /**
   * Callback called on value change
   */
  onChange?: (value: number[]) => void;
  /**
   * Callback called when the user finishes interacting with the slider
   * (e.g. on mouseup, touchend, or keyup). Useful for analytics or
   * expensive operations where only the final value matters.
   */
  onValueCommit?: (value: number[]) => void;
  /**
   * Value to be added or subtracted on each step
   */
  step?: number;
  /**
   * Current value of the slider
   * Must be an array of numbers if provided.
   */
  value?: number[];
};

export const Slider = ({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  disabled = false,
  initialValue = [0],
  max = 100,
  min = 0,
  onChange,
  onValueCommit,
  step = 1,
  value,
  'data-testid': dataTestId = 'ui-slider',
  ...otherProps
}: SliderProps): JSX.Element => {
  if (!(isNonEmptyString(ariaLabel) || isNonEmptyString(ariaLabelledby))) {
    throw new Error(
      'UI Slider: Sliders should have an accessible name. Add a label using the aria-label or aria-labelledby prop.',
    );
  }

  const values = value ?? initialValue;

  const handleValueChange = (newValue: number[]) => {
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <SliderContainer
      {...otherProps}
      data-testid={dataTestId}
    >
      <StyledSliderRoot
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        defaultValue={initialValue}
        disabled={disabled}
        max={max}
        min={min}
        onValueChange={handleValueChange}
        step={step}
        {...(onValueCommit ? { onValueCommit } : {})}
        {...(value ? { value } : {})}
      >
        <StyledSliderTrack data-testid={`${dataTestId}-track`}>
          <StyledSliderRange data-testid={`${dataTestId}-range`} />
        </StyledSliderTrack>
        {values.map((_, index) => (
          <StyledSliderThumb
            key={`thumb-${index}`} // eslint-disable-line react/no-array-index-key
            data-testid={`${dataTestId}-thumb-${index}`}
          />
        ))}
      </StyledSliderRoot>
    </SliderContainer>
  );
};

Slider.displayName = 'Slider';
